"use strict";
import { createAdaptiveRefetchContext } from "graphql/data/apollo/AdaptiveRefetch";
import { useAccount } from "hooks/useAccount";
import usePrevious from "hooks/usePrevious";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState
} from "react";
import { useFiatOnRampTransactions } from "state/fiatOnRampTransactions/hooks";
import {
  useActivityWebLazyQuery,
  useOnAssetActivitySubscription
} from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { logger } from "utilities/src/logger/logger";
import { v4 as uuidV4 } from "uuid";
const { Provider: AdaptiveAssetActivityProvider, useQuery: useAssetActivityQuery } = createAdaptiveRefetchContext();
const SubscriptionContext = createContext(void 0);
export function AssetActivityProvider({ children }) {
  const account = useAccount();
  const previousAccount = usePrevious(account.address);
  const isRealtimeEnabled = false;
  const [attempt, incrementAttempt] = useReducer((attempt2) => attempt2 + 1, 1);
  const subscriptionId = useMemo(uuidV4, [account, attempt]);
  const result = useOnAssetActivitySubscription({
    variables: { account: account.address ?? "", subscriptionId },
    skip: true,
    // Always skip to prevent GraphQL subscription calls
    onError: (error) => {
      logger.error(error, {
        tags: {
          file: "AssetActivityProvider",
          function: "useOnAssetActivitySubscription#onError"
        }
      });
      incrementAttempt();
    }
  });
  const fiatOnRampTransactions = useFiatOnRampTransactions();
  const [lazyFetch, query] = useActivityWebLazyQuery();
  const fetch = useCallback(() => {
    return Promise.resolve();
  }, []);
  return <SubscriptionContext.Provider value={result}><AdaptiveAssetActivityProvider query={query} fetch={fetch} stale={account.address !== previousAccount}>{children}</AdaptiveAssetActivityProvider></SubscriptionContext.Provider>;
}
export function useAssetActivitySubscription() {
  const value = useContext(SubscriptionContext);
  if (!value) {
    throw new Error("useAssetActivitySubscription must be used within an AssetActivityProvider");
  }
  return value;
}
function useSubscribedActivities() {
  const account = useAccount();
  const previousAccount = usePrevious(account.address);
  const [subscribedActivities, setSubscribedActivities] = useState([]);
  useEffect(() => {
    if (account.address !== previousAccount) {
      setSubscribedActivities([]);
    }
  }, [account.address, previousAccount]);
  const subscription = useAssetActivitySubscription();
  useEffect(() => {
    const subscribedActivity = subscription.data?.onAssetActivity;
    if (subscribedActivity) {
      setSubscribedActivities((prev) => [subscribedActivity, ...prev]);
    }
  }, [subscription.data?.onAssetActivity]);
  return subscribedActivities;
}
export function useAssetActivity() {
  const query = useAssetActivityQuery();
  const { loading, data } = query;
  const fetchedActivities = data?.portfolios?.[0]?.assetActivities;
  const subscribedActivities = useSubscribedActivities();
  const activities = useMemo(() => {
    if (!fetchedActivities) {
      return subscribedActivities;
    }
    return [...subscribedActivities, ...fetchedActivities];
  }, [subscribedActivities, fetchedActivities]);
  return { activities, loading };
}
